/**
 * RaceCircuitModule — the GRAND CIRCUIT on the client (capital wave phase 5,
 * 2026-09-16): the loop is built once in a worker and adopted; within the draw
 * distance a camera-following RIBBON (asphalt, centre line, red-white kerbs)
 * is rebuilt every 400 m of movement, with BARRIERS on both edges (instanced;
 * hover SOLIDS for every segment once, physics colliders by a 300 m add /
 * 400 m drop sweep) and the twelve SECTOR GANTRIES; while riding a hovercraft
 * within 400 m of the loop the LAP HUD shows the sector split, lap time, best
 * lap (localStorage), a wrong-way warning and a 3-2-1 countdown on the line.
 * No server state (spec §11).
 */
import * as THREE from 'three/webgpu';
import { uv, vec3, float, fract, abs, step, mix, select } from 'three/tsl';
import type { App, IModule } from 'enginex';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { activePreset } from '../lib/quality_tiers.js';
import { raceCircuit, adoptCircuit, peekCircuit, setCircuitBuildPolicy, progressAlong, sectorAt, TRACK_HALF_W, BARRIER_SEG_M, SAMPLE_M, type Circuit } from '../lib/race_circuit.js';
import { initLap, stepLap, fmtLap, type LapState, type LapEvent } from '../lib/race_lap.js';
import { addStructureSolid, clearStructureSolids, structureHitAt } from '../lib/structure_solids.js';
import { plank } from '../lib/settlement_dressing.js';
import { civilizationOf } from '../lib/biome_settlements.js';
import type { PhysicsModule } from './PhysicsModule.js';

const REBUILD_M = 400;
const KERB_W = 1.2;
const BARRIER_H = 1.2;
const COLLIDER_ADD_M = 300;
const COLLIDER_DROP_M = 400;
const BEST_KEY = 'atlas:circuit-best';

export class RaceCircuitModule implements IModule {
  readonly name = 'RaceCircuit';
  private app: App | null = null;
  private scene: THREE.Scene | null = null;
  private seed = '';
  private circuit: Circuit | null = null;
  private worker: Worker | null = null;
  private building = false;
  private buildMs = 0;
  private workerError = '';
  private root = new THREE.Group();
  private ribbon: THREE.Mesh | null = null;
  private barriers: THREE.InstancedMesh | null = null;
  private gantries = new THREE.Group();
  private last: { cx: number; cz: number; radius: number } | null = null;
  private solidsSite = 0;
  private solidsCount = 0;
  private liveColliders = new Set<string>();
  private lastSweepT = 0;
  private lap: LapState = initLap(RaceCircuitModule.readBest());
  private hud: HTMLDivElement | null = null;
  private hudHtml = '';
  private hudT = 0;
  private lastPos: { x: number; z: number; t: number } | null = null;
  private speed = 0;
  private lastEvents: string[] = [];
  private stats = { ribbonTris: 0, barriers: 0, gantries: 0 };
  private ribbonMat: THREE.MeshStandardNodeMaterial | null = null;
  private barrierMat: THREE.MeshStandardMaterial | null = null;

  constructor(private readonly physics: PhysicsModule) {}

  private static readBest(): number | null {
    try { const v = localStorage.getItem(BEST_KEY); return v ? Number(v) : null; } catch { return null; }
  }

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene as unknown as THREE.Scene;
    this.root.name = 'grand-circuit';
    this.scene.add(this.root);
    this.root.add(this.gantries);
    setCircuitBuildPolicy('async'); // the main thread never builds it synchronously
    app.world.addSystem(makeSystem('CircuitRender', Phase.Animation, 39, () => this.tick()));
    app.world.addSystem(makeSystem('CircuitHud', Phase.UI, 30, (dt) => this.hudTick(dt)));
    (window as unknown as Record<string, unknown>).__circuit = () => this.diag();
  }

  dispose(): void {
    this.worker?.terminate(); this.worker = null;
    this.clearGeometry();
    if (this.solidsSite) clearStructureSolids(this.solidsSite);
    for (const id of this.liveColliders) this.physics.removeStaticCollider(id);
    this.liveColliders.clear();
    this.hud?.remove(); this.hud = null;
    this.root.parent?.remove(this.root);
  }

  private commonsSeed(): string | null {
    const conn = atlas.conn;
    if (!conn) return null;
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') return w.terrainSeed;
    return null;
  }

  private diag(): Record<string, unknown> {
    const c = this.circuit;
    const pr = c ? progressAlong(this.seed, atlas.localX, atlas.localZ) : null;
    return {
      loaded: !!c, seed: this.seed, building: this.building, buildMs: Math.round(this.buildMs), workerError: this.workerError,
      length: c?.length ?? 0, sectors: c?.sectors.length ?? 0, attempts: c?.attempts ?? 0, damping: c?.damping ?? 0, problems: c?.problems ?? [],
      legs: c?.legs.map((l) => `${l.name} ${((l.s1 - l.s0) / 1000).toFixed(1)} km`) ?? [],
      start: c?.start ?? null,
      progress: pr, sector: c && pr ? sectorAt(c, pr.s).name : null,
      lap: { onTrack: this.lap.onTrack, sector: this.lap.sector, lapT: this.lap.lapStartT === null ? null : performance.now() - this.lap.lapStartT, lastLap: this.lap.lastLap, best: this.lap.best, wrongWay: this.lap.wrongWay, countdown: this.lap.countdown, armed: this.lap.armed, splits: this.lap.splits },
      ribbonTris: this.stats.ribbonTris, barriers: this.stats.barriers, gantries: this.stats.gantries,
      solids: this.solidsCount, colliders: this.liveColliders.size, hudVisible: !!this.hud && this.hud.style.display !== 'none',
      speed: Math.round(this.speed * 10) / 10, riding: atlas.hoverRide, events: this.lastEvents.slice(-8),
      barrierAt: (x: number, z: number, y: number) => structureHitAt(x, z, y, 0.5),
      rebuildNow: () => { if (this.circuit) this.rebuild(this.circuit, atlas.localX, atlas.localZ, this.radius()); return this.stats; },
      sweepNow: () => { this.sweepColliders(true); return this.liveColliders.size; },
    };
  }

  private radius(): number { return Math.max(1200, Math.min(3000, activePreset().farCull ?? 2000)); }

  private tick(): void {
    if (atlas.currentWorldId === null || atlas.currentWorldId !== atlas.commonsWorldId) { this.root.visible = false; return; }
    if (!this.seed) { const s = this.commonsSeed(); if (!s) return; this.seed = s; }
    if (!this.circuit) {
      const peeked = peekCircuit(this.seed);
      if (peeked) this.adopt(peeked);
      else if (!this.building) this.startWorker();
      return;
    }
    this.root.visible = true;
    const px = atlas.localX, pz = atlas.localZ;
    const radius = this.radius();
    if (!this.last || this.last.radius !== radius || Math.hypot(px - this.last.cx, pz - this.last.cz) >= REBUILD_M) this.rebuild(this.circuit, px, pz, radius);
    this.sweepColliders(false);
  }

  private startWorker(): void {
    this.building = true;
    try {
      this.worker = new Worker(new URL('../workers/circuit_worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (e: MessageEvent<{ circuit: Circuit | null; ms: number; error: string }>) => {
        this.buildMs = e.data.ms;
        this.workerError = e.data.error;
        this.building = false;
        if (e.data.circuit) { adoptCircuit(e.data.circuit); this.adopt(e.data.circuit); }
        this.worker?.terminate(); this.worker = null;
      };
      this.worker.onerror = (ev) => { this.workerError = String(ev.message ?? ev); this.building = false; };
      this.worker.postMessage({ seed: this.seed });
    } catch (e) {
      // No worker (a bare page): build here once — the sync fallback.
      this.workerError = String(e);
      setCircuitBuildPolicy('sync');
      const c = raceCircuit(this.seed);
      this.building = false;
      if (c) this.adopt(c);
    }
  }

  private adopt(c: Circuit): void {
    this.circuit = c;
    // Hover SOLIDS for every barrier segment, once (the registry bins by 128 m cells).
    this.solidsSite = -(c.site.id + 1);
    clearStructureSolids(this.solidsSite);
    let n = 0;
    const step = Math.round(BARRIER_SEG_M / SAMPLE_M);
    for (let i = 0; i < c.pts.length; i += step) {
      const p = c.pts[i], q = c.pts[(i + step) % c.pts.length];
      const dx = q.x - p.x, dz = q.z - p.z, L = Math.hypot(dx, dz) || 1;
      const nx = -dz / L, nz = dx / L;
      const yaw = Math.atan2(dx, dz) + Math.PI / 2;
      for (const side of [-1, 1]) {
        const mx = (p.x + q.x) / 2 + nx * side * (TRACK_HALF_W + 0.7), mz = (p.z + q.z) / 2 + nz * side * (TRACK_HALF_W + 0.7);
        addStructureSolid(this.solidsSite, { x: mx, z: mz, hx: L / 2, hz: 0.3, yaw, base: p.y - 1, top: p.y + 3 });
        n++;
      }
    }
    this.solidsCount = n;
  }

  // ── Geometry ──────────────────────────────────────────────────────────────
  private clearGeometry(): void {
    if (this.ribbon) { this.root.remove(this.ribbon); this.ribbon.geometry.dispose(); this.ribbon = null; }
    if (this.barriers) { this.root.remove(this.barriers); this.barriers.geometry.dispose(); this.barriers.dispose(); this.barriers = null; }
    for (const g of [...this.gantries.children]) { this.gantries.remove(g); }
  }

  private materials(): { ribbon: THREE.MeshStandardNodeMaterial; barrier: THREE.MeshStandardMaterial } {
    if (!this.ribbonMat) {
      const m = new THREE.MeshStandardNodeMaterial();
      m.roughness = 0.92; m.metalness = 0;
      // u = arc length / 20 m (stripes), v = 0..1 across (kerb, track, kerb).
      const u = uv().x, v = uv().y;
      const kerbF = float(KERB_W / (2 * (TRACK_HALF_W + KERB_W)));
      const kerb = v.lessThan(kerbF).or(v.greaterThan(float(1).sub(kerbF)));
      const kerbCol = mix(vec3(0.82, 0.12, 0.12), vec3(0.94, 0.94, 0.94), step(0.5, fract(u.mul(0.5))));
      const centre = abs(v.sub(0.5)).lessThan(0.006).and(fract(u).lessThan(0.55));
      const edge = abs(v.sub(kerbF)).lessThan(0.004).or(abs(v.sub(float(1).sub(kerbF))).lessThan(0.004));
      const asphalt = vec3(0.16, 0.165, 0.17);
      m.colorNode = select(kerb, kerbCol, select(centre.or(edge), vec3(0.9, 0.9, 0.88), asphalt));
      pinStaticMotionVectors(m);
      this.ribbonMat = m;
    }
    if (!this.barrierMat) { this.barrierMat = new THREE.MeshStandardMaterial({ color: 0xe8e8e8, roughness: 0.7 }); pinStaticMotionVectors(this.barrierMat); }
    return { ribbon: this.ribbonMat, barrier: this.barrierMat };
  }

  private rebuild(c: Circuit, cx: number, cz: number, radius: number): void {
    this.clearGeometry();
    this.last = { cx, cz, radius };
    const { ribbon: rm, barrier: bm } = this.materials();
    const n = c.pts.length;
    const r2 = radius * radius;
    const inRange = (i: number): boolean => { const p = c.pts[i]; const dx = p.x - cx, dz = p.z - cz; return dx * dx + dz * dz <= r2; };
    // Rows: 4 vertices across per in-range sample; quads between consecutive in-range samples.
    const pos: number[] = [], uvs: number[] = [], idx: number[] = [];
    const W = TRACK_HALF_W + KERB_W;
    const across = [-W, -TRACK_HALF_W, TRACK_HALF_W, W];
    const rowIndex = new Int32Array(n).fill(-1);
    let rows = 0;
    for (let i = 0; i < n; i++) {
      if (!inRange(i)) continue;
      const p = c.pts[i], q = c.pts[(i + 1) % n];
      const dx = q.x - p.x, dz = q.z - p.z, L = Math.hypot(dx, dz) || 1;
      const nx = -dz / L, nz = dx / L;
      const y = p.y + 0.06;
      for (let k = 0; k < 4; k++) { pos.push(p.x + nx * across[k], y, p.z + nz * across[k]); uvs.push(p.s / 20, (across[k] + W) / (2 * W)); }
      rowIndex[i] = rows++;
    }
    for (let i = 0; i < n; i++) {
      const a = rowIndex[i], b = rowIndex[(i + 1) % n];
      if (a < 0 || b < 0) continue;
      for (let k = 0; k < 3; k++) {
        const a0 = a * 4 + k, a1 = a0 + 1, b0 = b * 4 + k, b1 = b0 + 1;
        idx.push(a0, b0, a1, a1, b0, b1); // wound to face UP (+y): seen from above, counter-clockwise
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    this.ribbon = new THREE.Mesh(geo, rm);
    this.ribbon.receiveShadow = true;
    this.ribbon.frustumCulled = true;
    this.root.add(this.ribbon);
    this.stats.ribbonTris = idx.length / 3;
    // Barriers: one instance per 16 m per side, within the radius.
    const step = Math.round(BARRIER_SEG_M / SAMPLE_M);
    const mats: THREE.Matrix4[] = [];
    const m4 = new THREE.Matrix4(), q4 = new THREE.Quaternion(), p3 = new THREE.Vector3(), s3 = new THREE.Vector3(1, 1, 1), up = new THREE.Vector3(0, 1, 0);
    for (let i = 0; i < n; i += step) {
      if (!inRange(i)) continue;
      const p = c.pts[i], q = c.pts[(i + step) % n];
      const dx = q.x - p.x, dz = q.z - p.z, L = Math.hypot(dx, dz) || 1;
      const nx = -dz / L, nz = dx / L;
      const yaw = Math.atan2(dx, dz) + Math.PI / 2;
      for (const side of [-1, 1]) {
        p3.set((p.x + q.x) / 2 + nx * side * (TRACK_HALF_W + 0.7), (p.y + q.y) / 2 + BARRIER_H / 2, (p.z + q.z) / 2 + nz * side * (TRACK_HALF_W + 0.7));
        q4.setFromAxisAngle(up, yaw);
        s3.set(L, 1, 1);
        mats.push(m4.compose(p3, q4, s3).clone());
      }
    }
    if (mats.length > 0) {
      const im = new THREE.InstancedMesh(new THREE.BoxGeometry(1, BARRIER_H, 0.4), bm, mats.length);
      mats.forEach((m, i) => im.setMatrixAt(i, m));
      im.instanceMatrix.needsUpdate = true;
      im.castShadow = false; im.receiveShadow = true;
      this.barriers = im;
      this.root.add(im);
    }
    this.stats.barriers = mats.length;
    // Sector gantries within the radius: posts + beam + the sector's plank.
    const civ = civilizationOf(c.site.siteSeed);
    let g = 0;
    for (const sec of c.sectors) {
      const dx = sec.gate.x - cx, dz = sec.gate.z - cz;
      if (dx * dx + dz * dz > r2) continue;
      const grp = new THREE.Group();
      const dir = new THREE.Vector3(Math.sin(sec.gate.yaw), 0, Math.cos(sec.gate.yaw)); // the travel direction (yaw = atan2(dx, dz))
      const nrm = new THREE.Vector3(-dir.z, 0, dir.x);
      const postMat = new THREE.MeshStandardMaterial({ color: 0x3a3f48, roughness: 0.6 });
      for (const side of [-1, 1]) {
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.8, 9, 0.8), postMat);
        post.position.set(sec.gate.x + nrm.x * side * (TRACK_HALF_W + 2.5), sec.gate.y + 4.5, sec.gate.z + nrm.z * side * (TRACK_HALF_W + 2.5));
        grp.add(post);
      }
      const beam = new THREE.Mesh(new THREE.BoxGeometry(2 * (TRACK_HALF_W + 2.5) + 0.8, 1, 1), postMat);
      beam.position.set(sec.gate.x, sec.gate.y + 9.3, sec.gate.z);
      beam.rotation.y = Math.atan2(nrm.x, nrm.z) + Math.PI / 2;
      grp.add(beam);
      const pl = plank(sec.name.toUpperCase(), civ, Math.max(6, sec.name.length * 0.45), 1.1);
      pl.position.set(sec.gate.x, sec.gate.y + 8.1, sec.gate.z);
      pl.rotation.y = sec.gate.yaw + Math.PI; // faces the arriving craft
      grp.add(pl);
      this.gantries.add(grp);
      g++;
    }
    this.stats.gantries = g;
  }

  // ── Physics colliders: a near-ring sweep ──────────────────────────────────
  private sweepColliders(force: boolean): void {
    const c = this.circuit;
    if (!c) return;
    const now = performance.now();
    if (!force && now - this.lastSweepT < 500) return;
    this.lastSweepT = now;
    const px = atlas.localX, pz = atlas.localZ;
    const step = Math.round(BARRIER_SEG_M / SAMPLE_M);
    const n = c.pts.length;
    const want = new Set<string>();
    const add2 = COLLIDER_ADD_M * COLLIDER_ADD_M;
    for (let i = 0; i < n; i += step) {
      const p = c.pts[i];
      const dx = p.x - px, dz = p.z - pz;
      if (dx * dx + dz * dz > add2) continue;
      const q = c.pts[(i + step) % n];
      const ex = q.x - p.x, ez = q.z - p.z, L = Math.hypot(ex, ez) || 1;
      const nx = -ez / L, nz = ex / L;
      const yaw = Math.atan2(ex, ez) + Math.PI / 2;
      for (const side of [-1, 1]) {
        const id = `circuit:${i}:${side}`;
        want.add(id);
        if (this.liveColliders.has(id)) continue;
        const mx = (p.x + q.x) / 2 + nx * side * (TRACK_HALF_W + 0.7), mz = (p.z + q.z) / 2 + nz * side * (TRACK_HALF_W + 0.7);
        if (this.physics.addStaticCollider(id, { shape: 'box', halfExtents: [L / 2, BARRIER_H / 2, 0.2] }, { x: mx, y: (p.y + q.y) / 2 + BARRIER_H / 2, z: mz, yaw, scale: 1 })) this.liveColliders.add(id);
      }
    }
    const drop2 = COLLIDER_DROP_M * COLLIDER_DROP_M;
    for (const id of [...this.liveColliders]) {
      if (want.has(id)) continue;
      const i = Number(id.split(':')[1]);
      const p = c.pts[i];
      const dx = p.x - px, dz = p.z - pz;
      if (dx * dx + dz * dz > drop2) { this.physics.removeStaticCollider(id); this.liveColliders.delete(id); }
    }
  }

  // ── The lap HUD ───────────────────────────────────────────────────────────
  private ensureHud(): HTMLDivElement {
    if (this.hud) return this.hud;
    const el = document.createElement('div');
    el.id = 'circuit-hud';
    el.style.cssText = 'position:fixed;top:118px;left:16px;z-index:40;min-width:220px;padding:10px 14px;border-radius:var(--ax-radius);'
      + 'background:color-mix(in srgb, var(--ax-bg) 78%, transparent);border:1px solid var(--ax-line2);color:var(--ax-text);font:12px/1.45 var(--ax-mono);'
      + 'pointer-events:none;display:none;white-space:pre;';
    document.body.appendChild(el);
    this.hud = el;
    return el;
  }

  private hudTick(dt: number): void {
    const c = this.circuit;
    const now = performance.now();
    // Ground speed from the position deltas (works for any ride).
    if (this.lastPos) { const el = Math.max(1e-3, (now - this.lastPos.t) / 1000); const v = Math.hypot(atlas.localX - this.lastPos.x, atlas.localZ - this.lastPos.z) / el; this.speed = this.speed * 0.7 + Math.min(200, v) * 0.3; }
    this.lastPos = { x: atlas.localX, z: atlas.localZ, t: now };
    const riding = atlas.hoverRide && !!c && atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId;
    const pr = riding && c ? progressAlong(this.seed, atlas.localX, atlas.localZ) : null;
    const events: LapEvent[] = c ? stepLap(this.lap, riding ? pr : null, c.length, (s) => sectorAt(c, s).index, this.speed, Math.min(0.25, dt), now) : [];
    for (const e of events) {
      this.lastEvents.push(e.kind + (e.value !== undefined ? `:${Math.round(e.value)}` : ''));
      if (e.kind === 'lap' && this.lap.best !== null) { try { localStorage.setItem(BEST_KEY, String(this.lap.best)); } catch { /* private mode */ } }
    }
    if (this.lastEvents.length > 40) this.lastEvents.splice(0, this.lastEvents.length - 40);
    const el = this.ensureHud();
    const show = riding && this.lap.onTrack && !!c;
    if (!show) { if (el.style.display !== 'none') { el.style.display = 'none'; this.hudHtml = ''; } return; }
    this.hudT += dt;
    if (this.hudT < 0.1) return;
    this.hudT = 0;
    const sec = c!.sectors[this.lap.sector];
    const lapT = this.lap.lapStartT === null ? null : now - this.lap.lapStartT;
    const secT = this.lap.sectorStartT === null ? null : now - this.lap.sectorStartT;
    const lines = [
      `<b style="color:var(--ax-hi)">THE GRAND CIRCUIT</b>`,
      `${sec.name}   ${fmtLap(secT)}`,
      `lap   ${fmtLap(lapT)}${this.lap.armed || this.lap.lapStartT !== null ? '' : '   (cross the line)'}`,
      `last  ${fmtLap(this.lap.lastLap)}   best ${fmtLap(this.lap.best)}`,
    ];
    if (this.lap.wrongWay) lines.push(`<b style="color:var(--ax-bad)">WRONG WAY</b>`);
    if (this.lap.countdown !== null) lines.push(`<b style="font-size:28px;color:var(--ax-good)">${this.lap.countdown}</b>`);
    else if (this.lastEvents[this.lastEvents.length - 1] === 'go' && lapT !== null && lapT < 1500) lines.push(`<b style="font-size:22px;color:var(--ax-good)">GO</b>`);
    const html = lines.join('\n');
    if (html !== this.hudHtml) { this.hudHtml = html; el.innerHTML = html; }
    if (el.style.display === 'none') el.style.display = 'block';
  }
}
